import { useEffect, useState } from 'react'
import { ListPlus, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { liesMaterialListe, materialListeVorschau, type ListenFeld, type MaterialListeVorschau, type Spaltenzuordnung } from '../api/calculation-ai.api'
import { fetchRaster } from '../api/calculation-positions.api'
import type { CalcFile, CalcPosition } from '../types/calculation.types'

const FELDER: { feld: ListenFeld; name: string; pflicht?: boolean; hilfe?: string }[] = [
  { feld: 'bezeichnung', name: 'Bezeichnung', pflicht: true },
  { feld: 'menge', name: 'Menge / Stück', pflicht: true },
  { feld: 'einheit', name: 'Einheit', hilfe: 'ohne: Stk' },
  { feld: 'laenge', name: 'Länge', hilfe: 'mit Länge: laufende Meter' },
  { feld: 'gewicht', name: 'Gewicht (kg der Zeile)' },
  { feld: 'werkstoff', name: 'Werkstoff' },
  { feld: 'position', name: 'Position / Baugruppe', hilfe: 'Nummer oder OZ der Position' },
]

/**
 * AP-37 Detailblatt N 6.1, 12.2-26 (N2) — **Stückliste aus Excel oder CSV, ohne KI.** Spalten zuordnen
 * (Vorgabe: gemerkt je Kopfzeile, sonst aus den Überschriften erkannt), Zielposition für Zeilen ohne
 * passende Positionskennung, Vorschau der ersten Zeilen. „Vorschläge anlegen" legt die Zeilen als
 * Kostenzeilen-Vorschläge an — übernommen wird in der Vorschlagsliste.
 */
export function MaterialListeDialog({ kalkulationId, datei, onClose, onFertig }: { kalkulationId: string; datei: CalcFile; onClose: () => void; onFertig: () => void }) {
  const [v, setV] = useState<MaterialListeVorschau | null>(null)
  const [zuordnung, setZuordnung] = useState<Spaltenzuordnung>({})
  const [positionen, setPositionen] = useState<CalcPosition[]>([])
  const [ziel, setZiel] = useState('')
  const [merken, setMerken] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    void materialListeVorschau(kalkulationId, datei.id)
      .then((x) => {
        setV(x)
        setZuordnung(x.mapping)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Liste ließ sich nicht lesen.')))
    void fetchRaster(kalkulationId)
      .then((r) => {
        const p = r.positions.filter((x) => x.kind !== 'TEXT')
        setPositionen(p)
        if (p.length === 1) setZiel(p[0].id)
      })
      .catch(() => setPositionen([]))
  }, [kalkulationId, datei.id])

  async function lesen() {
    setLaeuft(true)
    setFehler(null)
    try {
      await liesMaterialListe(kalkulationId, { fileId: datei.id, mapping: zuordnung, positionId: ziel || null, remember: merken })
      onFertig()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Liste ließ sich nicht übernehmen.'))
      setLaeuft(false)
    }
  }

  const spalte = (f: ListenFeld) => (zuordnung[f] ? (v?.headers.indexOf(zuordnung[f]!) ?? -1) : -1)
  const bereit = !!zuordnung.bezeichnung && !!zuordnung.menge
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <ListPlus className="h-5 w-5 text-primary" /> Material aus Stückliste
          </h2>
          <button type="button" className="text-muted-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {datei.file.originalName} — ohne KI gelesen. Jede Zeile wird ein Vorschlag für eine Kostenzeile; übernommen wird in der Vorschlagsliste.
          {v?.remembered && ' Die Zuordnung ist für diese Kopfzeile gemerkt.'}
        </p>
        {!v && !fehler && <p className="mt-4 text-sm text-muted-foreground">Liest …</p>}
        {v && (
          <div className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto">
            <div className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
              {FELDER.map((f) => (
                <label key={f.feld} className="flex items-center gap-2">
                  <span className="w-40 shrink-0 text-muted-foreground">
                    {f.name}
                    {f.pflicht && ' *'}
                  </span>
                  <select className={`${inlineInput} min-w-0 flex-1`} value={zuordnung[f.feld] ?? ''} onChange={(e) => setZuordnung({ ...zuordnung, [f.feld]: e.target.value || undefined })}>
                    <option value="">{f.hilfe ? `— (${f.hilfe})` : '—'}</option>
                    {v.headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  {f.feld === 'laenge' && zuordnung.laenge && (
                    <select className={`${inlineInput} !w-16`} value={zuordnung.laengeEinheit ?? 'mm'} onChange={(e) => setZuordnung({ ...zuordnung, laengeEinheit: e.target.value as 'mm' | 'm' })} aria-label="Einheit der Länge">
                      <option value="mm">mm</option>
                      <option value="m">m</option>
                    </select>
                  )}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <span className="w-40 shrink-0 text-muted-foreground">Zielposition</span>
              <select className={`${inlineInput} min-w-0 flex-1`} value={ziel} onChange={(e) => setZiel(e.target.value)}>
                <option value="">— nur Zeilen mit passender Positionskennung —</option>
                {positionen.map((p) => (
                  <option key={p.id} value={p.id}>
                    Pos. {`${p.displayNumber} ${p.shortText}`.trim()}
                  </option>
                ))}
              </select>
            </label>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left">
                    {v.headers.map((h, i) => {
                      const feld = FELDER.find((f) => spalte(f.feld) === i)
                      return (
                        <th key={h} className="px-2 py-1 font-medium">
                          {h}
                          {feld && <span className="block font-normal text-primary">→ {feld.name}</span>}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {v.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      {v.headers.map((h, j) => (
                        <td key={h} className="whitespace-nowrap px-2 py-1">
                          {r[j]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              {v.rowCount} Zeilen{v.rowCount > v.rows.length ? `, die ersten ${v.rows.length} gezeigt` : ''}.
            </p>
          </div>
        )}
        {fehler && <p className="mt-3 text-sm text-destructive">{fehler}</p>}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={merken} onChange={(e) => setMerken(e.target.checked)} /> Zuordnung für diese Kopfzeile merken
          </label>
          <span className="flex gap-2">
            <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
              Abbrechen
            </button>
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!v || !bereit || laeuft} onClick={() => void lesen()}>
              <ListPlus className="h-4 w-4" /> Vorschläge anlegen
            </button>
          </span>
        </div>
      </div>
    </div>
  )
}
